/**
 * Utility para gerar código de incorporação de vídeo.
 *
 * Com a migração do iframe → Web Component (<hostvsl-player>), o embed virou
 * minimalista: o cliente cola 2 tags e o componente faz todo o resto em
 * runtime (headline, action button, responsive_config, timing, TESTHEADLINE).
 * Toda a lógica de buildHeadlineBlock/buildActionButtonBlock/padding-hack
 * antiga vive agora dentro do próprio `e-player-js/hostvsl-player.js`.
 */

// Interface para os dados do videoInformations.js. Continua usada por outras
// telas (preview no autoplay wizard, speed-testing) — por isso não é dead code.
export interface VideoInformations {
  id_video: string;
  id_user: string;
  videoWidth: string;
  videoHeight: string;
  haveHeadline: boolean;
  headlineText: string;
  subHeadlineText: string;
  headlineColor: string;
  subHeadlineColor: string;
  headlineSize: string | number;
  subHeadlineSize: string | number;
  headlineTypeFont: string;
  headlineFontWeight: string;
  haveDelayButton: boolean;
  textDelayButton: string;
  sizeDelayButton: string;
  linkDelayButton: string;
  initialDelay: string;
  endDelay: string;
  showAllTimeDelay: boolean;
  backgroundDelayButton: string;
  textColorDelayButton: string;
  backgroundDelayButtonHover: string;
  textColorDelayButtonHover: string;
  actionButtonMode: string;
  headlineShowThroughout?: boolean;
  headlineInitialDelay?: string;
  headlineEndDelay?: string;
  autoplayPositionXPercent?: number | null;
  autoplayPositionYPercent?: number | null;
  autoplayWidthPercent?: number | null;
  autoplayHeightPercent?: number | null;
  [key: string]: unknown;
}

// Tipos para as configurações (mantidos por compatibilidade — outras telas
// consomem essa interface ainda).
export interface HeadlineConfig {
  enabled: boolean;
  text: string;
  color: string;
  size: 'pequeno' | 'medio' | 'grande';
  fontFamily: string;
  fontWeight: string;
  subHeadlineText?: string;
  subHeadlineColor?: string;
  subHeadlineSize?: 'pequeno' | 'medio' | 'grande';
}

export interface ActionButtonConfig {
  enabled: boolean;
  mode: 'bellowVideo' | 'insideVideo' | null;
  text: string;
  link: string;
  size: 'pequeno' | 'medio' | 'grande';
  backgroundColor: string;
  textColor: string;
  backgroundHover: string;
  textColorHover: string;
  initialDelay: string;
  endDelay: string;
  showAllTime: boolean;
}

// Campos extras aceitos por retrocompatibilidade com chamadas existentes.
// Com Web Component, nenhum desses é necessário no embed (o componente lê
// videoInformations.js em runtime), mas os calleres antigos ainda passam.
export interface VideoEmbedConfig {
  videoId: string;
  userId: string;
  videoOrigin?: string;
  videoWidth?: string | number;
  videoHeight?: string | number;
  haveHeadline?: boolean;
  isHTMLSelected?: boolean;
  headlineHtmlCode?: string;
  headlineText?: string;
  subHeadlineText?: string;
  headlineColor?: string;
  subHeadlineColor?: string;
  headlineSize?: string | number;
  subHeadlineSize?: string | number;
  headlineTypeFont?: string;
  headlineFontWeight?: string;
  headlineShowThroughout?: boolean;
  headlineInitialDelay?: string;
  headlineEndDelay?: string;
}

const getEnvVars = () => ({
  bucketCdn: import.meta.env.VITE_BUCKET_CDN || '',
  playerUrl: import.meta.env.VITE_API_PLAYJS_BUNNY || '',
  playerVersion: import.meta.env.VITE_API_PLAYJS_VERSION || '',
});

/**
 * Busca as informações completas do vídeo do CDN. Usada por preview de outras
 * telas (autoplay wizard, speed-testing). NÃO é mais usada na geração de
 * embed — o Web Component faz o fetch em runtime na máquina do visitante.
 */
export const fetchVideoInformations = async (
  userId: string,
  videoId: string,
): Promise<VideoInformations | null> => {
  const { bucketCdn } = getEnvVars();
  const url = `${bucketCdn}${userId}/${videoId}/videoInformations.js?${Date.now()}`;

  try {
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return null;
    const text = await response.text();
    // Backend grava no formato:
    //   const objeto = { ... };
    //   window.videoFileInformations = objeto;
    const objetoMatch = text.match(
      /const\s+objeto\s*=\s*(\{[\s\S]*\})\s*;\s*window\.videoFileInformations/,
    );
    if (objetoMatch && objetoMatch[1]) {
      try {
        return JSON.parse(objetoMatch[1]) as VideoInformations;
      } catch {
        return null;
      }
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Gera o snippet do Web Component que o cliente cola na landing. Duas linhas:
 *   <hostvsl-player id="vid-XYZ" data-video="...">
 *   <script async src="...">
 *
 * O componente cuida de: headline, action button (acima/abaixo do vídeo),
 * responsive_config, timing show/hide, e TESTHEADLINE — tudo em runtime
 * lendo videoInformations.js.
 */
export const generateJsCode = async (config: VideoEmbedConfig): Promise<string> => {
  const { videoId, userId, videoOrigin = 'ORIGINAL' } = config;
  const { bucketCdn, playerUrl, playerVersion } = getEnvVars();
  const videoDataUrl = `${bucketCdn}${userId}/${videoId}/videoInformations.js?VIDEO_ORIGIN=${videoOrigin}`;
  const originAttr = videoOrigin !== 'ORIGINAL' ? ` data-video-origin="${videoOrigin}"` : '';
  const scriptSrc = `${playerUrl}${playerVersion}/hostvsl-player.js`;
  return `<hostvsl-player id="vid-${videoId}" data-video="${videoDataUrl}"${originAttr}></hostvsl-player>
<script async src="${scriptSrc}"></script>`;
};

/**
 * Mantido pela assinatura — alguns callers ainda destructuram `iframeCode`.
 * Como o iframe foi substituído pelo Web Component, retorna o mesmo snippet
 * do `jsCode` (alias de retrocompatibilidade).
 */
export const generateIframeCode = async (
  userId: string,
  videoId: string,
  _paddingTop: string,
  videoOrigin: string = 'ORIGINAL',
): Promise<string> => {
  return generateJsCode({ videoId, userId, videoOrigin });
};

export interface EmbedCodes {
  jsCode: string;
  iframeCode: string;
}

/**
 * Função principal — gera o snippet a partir do videoId/userId/videoOrigin.
 * Não precisa mais fetchar videoInformations.js no admin: o componente fetcha
 * em runtime e aplica toda a config (headline, button, responsive, timing).
 */
export const generateVideoEmbedCodes = async (config: VideoEmbedConfig): Promise<EmbedCodes> => {
  const jsCode = await generateJsCode(config);
  return {
    jsCode,
    iframeCode: jsCode,
  };
};

export default {
  generateVideoEmbedCodes,
  generateJsCode,
  generateIframeCode,
  fetchVideoInformations,
};
